'use client';

import dynamic from 'next/dynamic';
import { memo, useContext } from 'react';
import { useIsNative } from '~/context/common/DeviceContext';
import UserContext, { useIsLoggedIn } from '~/context/common/UserContext';
import ModalContext from '~/context/home/ModalContext';

const SaveSearchModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "SaveSearchModal" */ './Modals/SaveSearch/SaveSearchModal'
    ),
  { ssr: false }
);

const SoftPullModal = dynamic(
  () =>
    import(/* webpackChunkName: "SoftPullModal" */ './Modals/SoftPullModal'),
  { ssr: false }
);

const AppraisalEmbeddedModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "AppraisalEmbeddedModal" */ './Modals/AppraisalEmbeddedModal'
    ),
  { ssr: false }
);

const AppraisalRefreshModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "AppraisalRefreshModal" */ './Modals/AppraisalRefreshModal'
    ),
  { ssr: false }
);

const MoreDetailsModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "MoreDetailsModal" */ './Modals/MoreDetailsModal/MoreDetailsModal'
    ),
  { ssr: false }
);

const GeicoDiscountModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "GeicoDiscountModal" */ '@/common/Modals/GeicoDiscountModal'
    ),
  { ssr: false }
);

const SampleTermsModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "SampleTermsModal" */ '@/common/helios/Modals/SampleTermsModal'
    ),
  { ssr: false }
);

const ShippingModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "ShippingModal" */ '@/common/helios/Modals/ShippingModal'
    ),
  { ssr: false }
);

type OwnProps = {
  loadAuthedModals: boolean;
  loadSoftPullMfe: boolean;
  loadAppraisalEmbeddedModal: boolean;
  loadAppraisalRefreshModal: boolean;
};

const ModalsContent = memo(
  ({
    loadAuthedModals,
    loadSoftPullMfe,
    loadAppraisalEmbeddedModal,
    loadAppraisalRefreshModal
  }: OwnProps) => {
    const {
      geicoDiscount,
      isGeicoDiscountModalOpen,
      isSampleTermsModalOpen,
      isShippingModalOpen,
      setGeicoDiscountModalOpen,
      setSampleTermsModalOpen,
      setShippingModalOpen
    } = useContext(ModalContext);
    const loadGeicoDiscountModal = Boolean(geicoDiscount);

    return (
      <>
        {loadAuthedModals && <SaveSearchModal />}
        {loadSoftPullMfe && <SoftPullModal />}
        {loadAppraisalEmbeddedModal && <AppraisalEmbeddedModal />}
        {loadAppraisalRefreshModal && <AppraisalRefreshModal />}
        <MoreDetailsModal />
        {loadGeicoDiscountModal && (
          <GeicoDiscountModal
            discount={geicoDiscount}
            isOpen={isGeicoDiscountModalOpen}
            setGeicoDiscountModalOpen={setGeicoDiscountModalOpen}
          />
        )}
        <SampleTermsModal
          isSampleTermsModalOpen={isSampleTermsModalOpen}
          setSampleTermsModalOpen={setSampleTermsModalOpen}
        />
        <ShippingModal
          isShippingModalOpen={isShippingModalOpen}
          setShippingModalOpen={setShippingModalOpen}
        />
      </>
    );
  }
);

export default function Modals() {
  const isLoggedIn = useIsLoggedIn();
  const {
    userState: { isLoaded }
  } = useContext(UserContext);
  const isNative = useIsNative();
  const isLoggedAndLoaded = isLoaded && isLoggedIn;

  return (
    <ModalsContent
      loadAuthedModals={isLoggedAndLoaded}
      loadSoftPullMfe={!isNative}
      loadAppraisalEmbeddedModal={isLoggedAndLoaded}
      loadAppraisalRefreshModal={isLoggedAndLoaded}
    />
  );
}
